import {useIssueDeletionState} from '@github-ui/issue-viewer/useIssueDeletionState'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'

export function useIssueDeletionCompletion({
  owner,
  repo,
  number,
  refresh,
  closePane,
}: {
  owner: string
  repo: string
  number: number
  refresh?: () => Promise<void>
  closePane: (opts: {
    force: boolean
    returnFocusToProject?: boolean
    bypassIssueViewerNavigationBlocker?: boolean
  }) => Promise<boolean>
}) {
  const scope = useMemo(() => ({owner, repo, number}), [owner, repo, number])
  const deletions = useIssueDeletionState()
  const queryClient = useQueryClient()
  const activeScopeRef = useRef<typeof scope | null>(null)
  const activeScopeStartIdRef = useRef(0)
  const seenDeletionsRef = useRef(new Set<number>())
  useEffect(() => {
    activeScopeRef.current = scope
    activeScopeStartIdRef.current = queryClient
      .getMutationCache()
      .getAll()
      .reduce((maxId, mutation) => Math.max(maxId, mutation.mutationId), 0)
    return () => {
      activeScopeRef.current = null
    }
  }, [queryClient, scope])

  const onIssueDelete = useCallback(async () => {
    await refresh?.()
  }, [refresh])

  // The pane closes as soon as the user confirms, so this runs before the request settles. Never
  // close twice, or let a callback retained by an old mutation close a newly opened issue.
  const closePaneForIssueDeletion = useCallback(() => {
    if (activeScopeRef.current !== scope) return
    activeScopeRef.current = null
    void closePane({force: true, returnFocusToProject: true, bypassIssueViewerNavigationBlocker: true})
  }, [closePane, scope])

  useEffect(() => {
    for (const deletion of deletions) {
      if (
        deletion.id <= activeScopeStartIdRef.current ||
        seenDeletionsRef.current.has(deletion.id) ||
        deletion.owner !== owner ||
        deletion.repository !== repo ||
        deletion.number !== number
      ) {
        continue
      }
      seenDeletionsRef.current.add(deletion.id)
      closePaneForIssueDeletion()
    }
  }, [closePaneForIssueDeletion, deletions, number, owner, repo])

  return {onIssueDelete, closePaneForIssueDeletion}
}
